import { SurfReport, WAVE_SIZES, CONDITIONS, CROWD_LEVELS } from "@/types/surfReport";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PlaceIcon from "@mui/icons-material/Place";
import { Avatar, Box, Card, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import VoteButtons from "./VoteButtons";

interface MiniReportCardProps {
  report: SurfReport;
  onClick?: () => void;
  /** When provided, renders inline confirm/deny controls that open the
   *  auth prompt if the user isn't signed in. */
  onAuthRequired?: () => void;
}

function getTimeAgo(report: SurfReport): string {
  if (!report.createdAt?.toDate) return "now";
  return formatDistanceToNow(report.createdAt.toDate(), { addSuffix: false });
}

export default function MiniReportCard({
  report,
  onClick,
  onAuthRequired,
}: MiniReportCardProps) {
  const conditionConfig = CONDITIONS[report.conditions];
  const crowdConfig = CROWD_LEVELS[report.crowd];
  const hasMedia = !!report.mediaURL && report.mediaType === "photo";
  const likeCount = report.likeCount ?? 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.15s",
        "&:hover": onClick ? { borderColor: "rule.strong" } : {},
      }}
    >
      {hasMedia && (
        <Box
          sx={{
            width: "100%",
            height: 130,
            overflow: "hidden",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <img
            src={report.mediaURL!}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          p: 1.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        {/* Wave size — editorial serif */}
        <Typography variant="h4" sx={{ color: "text.primary", lineHeight: 1.1 }}>
          {WAVE_SIZES[report.waveSize].label}
        </Typography>

        {/* Conditions + crowd — dot + overline label */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <LabelDot color={conditionConfig.color} label={conditionConfig.label} />
          <LabelDot color={crowdConfig.color} label={crowdConfig.label} />
        </Box>

        {/* Comment preview */}
        {report.comment && (
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "text.primary",
              fontStyle: "italic",
              lineHeight: 1.4,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            &ldquo;{report.comment}&rdquo;
          </Typography>
        )}

        {/* Footer: user + meta, separated by hairline rule */}
        <Box
          sx={{
            mt: "auto",
            pt: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <Avatar
              src={report.userPhotoURL || undefined}
              sx={{ width: 20, height: 20 }}
            />
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 500,
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {report.userDisplayName}
            </Typography>
            {likeCount > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <FavoriteIcon sx={{ fontSize: 11, color: "buoy.main" }} />
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                  {likeCount}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: 14,
            }}
          >
            {report.cameraName ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  minWidth: 0,
                }}
              >
                <PlaceIcon sx={{ fontSize: 11, color: "text.secondary" }} />
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {report.cameraName}
                </Typography>
              </Box>
            ) : (
              <Box />
            )}
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.6rem",
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {getTimeAgo(report)}
            </Typography>
          </Box>

          {/* Inline corroboration — lets the reader confirm or deny without
              leaving the home page. Stop click from bubbling up to the
              card's navigate-to-feed handler. */}
          {onAuthRequired && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.58rem",
                  color: "text.secondary",
                  letterSpacing: "0.08em",
                }}
              >
                Matches?
              </Typography>
              <VoteButtons
                reportId={report.id}
                confirmCount={report.confirmCount}
                denyCount={report.denyCount}
                onAuthRequired={onAuthRequired}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

function LabelDot({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="overline"
        sx={{
          fontSize: "0.65rem",
          color: "text.secondary",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
